import React from 'react';
import { ChevronDown } from 'lucide-react';

// Historical aggregate audit, rechecked 29 July 2026. These are not live counters.
// Source: docs/design/prototypes/proof-strip-2026-07/DATA.md, sections
// "The run", "The verdicts", "The purchase record" and "When the work happens".
const RESEARCH = { proceed: 29, closer: 9, walk: 1, medianSeconds: 96, freshRuns: 27 };
const PAPERWORK = { emails: 258, attachments: 1283, deadlineWatches: 22 };
const HOURS = { outside: 62, total: 128 };

export function ProofReceipt({ children }) {
  return <details className="lc-receipt">
    <summary>Source &amp; sample<ChevronDown size={15} aria-hidden="true" /></summary>
    <div className="lc-receipt-body">{children}</div>
  </details>;
}

export function StrategyDepth() {
  return <div className="lc-strategy-depth lc-enter">
    <div><span className="lc-small">Your goal, tested</span><h3>Put a year on your goal.</h3>
      <p>Model when your portfolio could support your income goal. See how higher rates, slower growth and rental changes affect the plan.</p>
    </div>
    <div className="lc-plan-working">
      <ol aria-label="How your freedom-year plan takes shape"><li>Your financial position</li><li>A property strategy</li><li>Your projected freedom year</li></ol>
      <div className="lc-scenario-line"><span>Test the assumptions</span><span>Interest rates</span><span>Growth</span><span>Rental yield</span></div>
      <p>Built around your capital, income goal, tax rate, existing portfolio and risk preferences.</p>
    </div>
  </div>;
}

export function ResearchRecord() {
  const total = RESEARCH.proceed + RESEARCH.closer + RESEARCH.walk;
  return <div className="lc-record lc-research-record lc-enter" aria-label="Historical research results">
    <div className="lc-record-heading"><span className="lc-small">What the early record shows</span><time dateTime="2026-07-29">July 2026 record</time></div>
    <div className="lc-record-columns">
      <figure className="lc-outcomes"><div className="lc-record-finding"><strong>1 in 4</strong><p>research runs returned a closer-look or walk-away recommendation.</p></div>
        <div className="lc-outcome-bar" role="img" aria-label="29 proceed, 9 look closer and 1 walk away, across 39 research runs">
          <span style={{ flex: RESEARCH.proceed }} /><span style={{ flex: RESEARCH.closer }} /><span style={{ flex: RESEARCH.walk }} />
        </div>
        <figcaption className="lc-outcome-labels"><span>Proceed</span><span>Look closer</span><span>Walk away</span></figcaption>
      </figure>
      <div className="lc-research-time"><strong>{RESEARCH.medianSeconds}<small>seconds</small></strong><p>Median research time for a fresh address in the recorded sample.</p></div>
    </div>
    <ProofReceipt><p><strong>Source:</strong> recorded product-activity audit, rechecked 29 July 2026.</p>
      <p><strong>Recommendations:</strong> {RESEARCH.closer + RESEARCH.walk} of {total} runs through 27 July returned “look closer” ({RESEARCH.closer}) or “walk away” ({RESEARCH.walk}). Rounded to one in four. Runs can include repeat properties; these are recommendations, not purchases prevented.</p>
      <p><strong>Timing:</strong> {RESEARCH.freshRuns} fresh-address runs, excluding cached results. Median {RESEARCH.medianSeconds} seconds. This historical sample is not a guarantee for the next assessment.</p>
    </ProofReceipt>
  </div>;
}

export function PurchaseRecord() {
  return <div className="lc-record lc-paperwork-record lc-enter" aria-label="Historical paperwork results">
    <div><span className="lc-small">Paperwork already read</span><div className="lc-paperwork-count"><strong>{PAPERWORK.attachments.toLocaleString('en-AU')}</strong><span>attachments</span></div></div>
    <div><p>Alongside <strong>{PAPERWORK.emails} emails</strong> received and <strong>{PAPERWORK.deadlineWatches} deadline watches</strong> recorded.</p><time dateTime="2026-07-29">July 2026 record</time>
      <ProofReceipt><p><strong>Source:</strong> stored purchase-record audit, rechecked 29 July 2026. All-time totals at that date, excluding the founder’s account.</p><p>Attachments read, emails received and deadline watches are separate counts. They do not establish that every purchase completed or every deadline was met.</p></ProofReceipt>
    </div>
  </div>;
}

export function PortfolioWatch() {
  const outsidePercent = Math.round(HOURS.outside / HOURS.total * 100);
  return <section className="lc-section lc-ownership-section" id="ownership"><div className="container">
    <div className="lc-ownership-layout">
      <div className="lc-copy lc-enter"><div className="lc-label"><span>04</span>Beyond settlement</div>
        <h2>Your portfolio.<br /><em>Still your plan.</em></h2>
        <p>Keep watch on equity, concentration and changes to your freedom year. Review which properties to hold or sell as your position changes.</p>
        <div className="lc-memory-copy"><h3>You don’t start from scratch.</h3><p>Your goals, preferences and purchase history carry forward. Inspect what Heyward remembers, including its source, and confirm, reject or delete it.</p></div>
      </div>
      <div className="lc-ownership-brief lc-enter" aria-label="Illustrative portfolio brief">
        <div className="lc-demo-heading"><span>Your morning brief</span><span>Illustration</span></div>
        <h3>What needs<br />your attention.</h3>
        <dl><div><dt>Equity</dt><dd>Know when your position opens room for the next move.</dd></div><div><dt>Portfolio balance</dt><dd>See when one market carries too much of your exposure.</dd></div><div><dt>Your goal</dt><dd>Review the plan when your projected freedom year changes.</dd></div></dl>
        <p className="lc-brief-signoff">One brief, with your properties and preferences in view.</p>
      </div>
    </div>
    <div className="lc-after-hours lc-enter">
      <div><strong>{outsidePercent}%</strong><p>of recorded research happened outside office hours.</p></div>
      <div><time dateTime="2026-07-29">July 2026 record</time><ProofReceipt><p><strong>Source:</strong> recorded product-activity audit, rechecked 29 July 2026. {HOURS.outside} of {HOURS.total} research actions occurred outside Monday–Friday, 9am–5pm, in the Australia/Sydney time zone.</p><p>Includes address searches, suburb searches and property assessments. This measures research activity, not autonomous overnight portfolio work.</p></ProofReceipt></div>
    </div>
  </div></section>;
}
